import { useCallback, useState, useEffect, useRef } from "react";
import { supabase } from "@/utils/supabase";
import { throttleTime, Subject } from "rxjs";
import type { CursorPosition } from "../config/types";
import { generateId } from "@/utils";

/**
 * 실시간 커서 공유 훅
 * RxJS throttle + Supabase Broadcast
 */
/**
 * 실시간 커서 공유 훅
 * RxJS throttle + Supabase Broadcast
 */
export function useRealtimeCursors(userIdToCheck: string | undefined, userColor?: string) {
  const [cursors, setCursors] = useState<CursorPosition[]>([]);
  const [myCursor, setMyCursor] = useState<{
    gridX: number;
    gridZ: number;
    color?: string;
  } | null>(null);
  
  // userId를 ref로 관리하되, props 변경 시 업데이트 및 leave 이벤트 전송
  const userIdRef = useRef<string>(userIdToCheck || generateId());
  const userColorRef = useRef<string>(userColor || "#4a90e2"); 
  const cursorSubject = useRef<Subject<{ gridX: number; gridZ: number }>>(
    new Subject()
  );
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  // userId 변경 처리
  useEffect(() => {
    const newId = userIdToCheck || generateId();
    if (userIdRef.current !== newId) {
      // 이전 ID로 leave 전송
      channelRef.current?.send({
        type: "broadcast",
        event: "cursor_leave",
        payload: { user_id: userIdRef.current },
      });
      userIdRef.current = newId;
    }
  }, [userIdToCheck]);

  // userColor 변경 시 ref 업데이트
  useEffect(() => {
    userColorRef.current = userColor || "#4a90e2";
  }, [userColor]);

  // 채널 설정 (한 번만 실행)
  useEffect(() => {
    const channel = supabase.channel("cursors", {
      config: { broadcast: { self: false } },
    });

    channel
      .on("broadcast", { event: "cursor" }, ({ payload }) => {
        const { user_id, grid_x, grid_z, color } = payload;
        
        // 내 커서는 무시 (혹시 self: true일 경우 대비)
        if (user_id === userIdRef.current) return;

        // console.log("[RealtimeCursors] Received cursor:", { user_id, grid_x, grid_z, color });

        setCursors((prev) => {
          const existing = prev.findIndex((c) => c.userId === user_id);
          const newCursor: CursorPosition = {
            userId: user_id,
            gridX: grid_x,
            gridZ: grid_z,
            color: color || "#ff6b6b",
          };

          if (existing >= 0) {
            const updated = [...prev];
            updated[existing] = newCursor;
            return updated;
          }

          return [...prev, newCursor];
        });
      })
      .on("broadcast", { event: "cursor_leave" }, ({ payload }) => {
        setCursors((prev) => prev.filter((c) => c.userId !== payload.user_id));
      })
      .subscribe();

    channelRef.current = channel;

    // RxJS throttle 설정 (200ms)
    const subscription = cursorSubject.current
      .pipe(throttleTime(200))
      .subscribe(({ gridX, gridZ }) => {
        const payload = {
          user_id: userIdRef.current, // 최신 ID 사용
          grid_x: gridX,
          grid_z: gridZ,
          color: userColorRef.current, 
        };

        // console.log("[RealtimeCursors] Sending cursor:", payload);

        channel.send({
          type: "broadcast",
          event: "cursor",
          payload,
        });
      });

    return () => {
      channel.send({
        type: "broadcast",
        event: "cursor_leave",
        payload: { user_id: userIdRef.current },
      });
      subscription.unsubscribe();
      supabase.removeChannel(channel);
    };
  }, []); // 의존성 최소화 (userIdRef 사용)

  // 커서 위치 업데이트
  const updateCursor = useCallback((gridX: number, gridZ: number) => {
    setMyCursor({ gridX, gridZ, color: userColorRef.current });
    cursorSubject.current.next({ gridX, gridZ });
  }, []);

  // 커서 숨기기
  const hideCursor = useCallback(() => {
    setMyCursor(null);
    channelRef.current?.send({
      type: "broadcast",
      event: "cursor_leave",
      payload: { user_id: userIdRef.current },
    });
  }, []);

  return {
    cursors,
    myCursor,
    updateCursor,
    hideCursor,
  };
}
